import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import { useFileManager } from 'api/file-manager/file-manager-context';
import panic from 'errors/panic';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import toBase64 from 'utils/to-base64';
import { RevisionActionProps } from 'components/tables/data/revision/types';
import { noop } from 'lodash';
import slugify from 'slugify';
import useDocumentGeneratorV2 from 'api/document-generator-v2/use-document-generator-v2';
import { IAutomationSetFileName, IPipeline } from 'pages/revisions-module/template-editor/editors/pipeline/types';

/**
 * Implements the confirm action for revision.
 */
export default function useConfirmAction() {
  const { getAction, fullName, organizationName, phoneNumber, email } = useApi();
  const { uploadFile } = useFileManager();
  const { generatePdf } = useDocumentGeneratorV2();

  /**
   * Makes a call to the API to confirm the revision.
   */
  async function confirmRevision({
    revision: { revisionId, kind },
    onStatusChange = noop,
    confirmations = {},
  }: RevisionActionProps) {
    try {
      const getRevision = getAction('RevisionGet');
      const revisionConfirmAction = getAction('RevisionCustomerConfirm');

      const revision = await getRevision({ parameters: { revisionId: String(revisionId) } });
      const { revisionData, revisionName, revisionTemplate } = revision;

      if (!revisionData) {
        throw new Error(kind === 'revision' ? 'Revízna správa nemá dáta' : 'Formulár nemá dáta');
      }

      if (!revisionTemplate) {
        throw new Error(kind === 'revision' ? 'Revízna správa nemá šablónu' : 'Formulár nemá šablónu');
      }

      const data = JSON.parse(revisionData);
      const pipeline = JSON.parse(revisionTemplate.pipelineScheme || 'null') as IPipeline | null;

      const setFileNameAutomation =
        (pipeline?.automations ?? []).find(
          (a): a is IAutomationSetFileName => a.type === 'set-file-name' && a.statusSource === 7 && a.statusTarget === 8
        ) ?? null;

      let fileName: string;

      if (setFileNameAutomation) {
        const fieldValue = String(data.fields?.[setFileNameAutomation.inputName] ?? '');
        const fieldValueWithoutPdfSuffix = fieldValue.replace(/\.pdf$/, '');
        const prefix = setFileNameAutomation.timestampPrefix ? `${Date.now()}-` : '';
        fileName = `${prefix}${fieldValueWithoutPdfSuffix}.pdf`;
      } else if (kind === 'revision') {
        fileName = `${Date.now()}-${slugify(revisionName)}.pdf`;
      } else {
        fileName = `${slugify(revisionName)}-ID-${revisionId}.pdf`;
      }

      const extendedData = {
        ...data,
        fields: {
          ...data.fields,
          _confirmerName: fullName,
          _confirmerOrganization: organizationName,
          _confirmerPhone: phoneNumber,
          _confirmerEmail: email,
          _confirmerDate: new Date().toLocaleDateString('sk-SK'),
          _confirmerTime: new Date().toLocaleTimeString('sk-SK'),
          _confirmations: confirmations,
        },
      };

      // generate the PDF
      const contents = await generatePdf(revision, extendedData);

      // upload the file to File Manager
      const base64Contents = await toBase64(contents);
      const remoteId = await uploadFile({ fileName, contents: base64Contents });

      // save it to DB
      const confirmActionResponse = await revisionConfirmAction({
        parameters: { revisionId: String(revisionId) },
        payload: { fileId: remoteId, fileName },
      });

      onStatusChange({ revisionId, status: confirmActionResponse.status, fileId: remoteId });

      showNotification({
        title: revision.kind === 'revision' ? 'Revízna správa bola prebratá' : 'Formulár bol prebratý',
        message:
          revision.kind === 'revision'
            ? `Revíznu správu ${revisionName} ste úspešne prebrali.`
            : `Formulár ${revisionName} ste úspešne prebrali.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
    } catch (e: any) {
      panic(e);
    }
  }

  return {
    confirmRevision,
  };
}
